import { PropsWithChildren } from 'react';

import { IconCornerDownRight, IconWorld } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { TextField, TextFieldType, Typography } from '@cfa/react-components';

import { cn } from '@/utils/tailwind';

import SquareIconButton from 'components/SquareIconButton/SquareIconButton';

import {
  CustomQuizAnswerDefinitionDto,
  PlanUrlSet,
} from '@/_generated/api-clients/xp';

type TranslationsItemProps = PropsWithChildren<{
  answers?: CustomQuizAnswerDefinitionDto[];
  id?: number;
  name: string;
  notes?: string;
  notesTranslation?: string;
  onChangeAnswer?: (e: React.ChangeEvent<TextFieldType>, index: number) => void;
  onChangeName?: (e: React.ChangeEvent<TextFieldType>) => void;
  onChangeNotes?: (e: React.ChangeEvent<TextFieldType>) => void;
  onChangeUrl?: (e: React.ChangeEvent<TextFieldType>, index: number) => void;
  onTranslateAnswer?: (index: number) => void;
  onTranslateName?: () => void;
  onTranslateNotes?: () => void;
  targetLanguage: string;
  translation: string;
  type: string;
  urls?: PlanUrlSet[];
  userLanguage: string;
}>;

interface ColumnContainerStyleProps {
  indented?: boolean;
}

const TranslationsItem: React.FC<TranslationsItemProps> = ({
  answers,
  children,
  id,
  name,
  notes,
  notesTranslation,
  onChangeAnswer,
  onChangeName,
  onChangeNotes,
  onChangeUrl,
  onTranslateAnswer,
  onTranslateName,
  onTranslateNotes,
  targetLanguage,
  translation,
  type,
  urls,
  userLanguage,
}) => {
  const { t } = useTranslation();
  return (
    <div data-testid={`${name}-${type}`}>
      <StyledColumnContainer>
        <StyledRowContainer>
          <StyledLeftSideSectionContainer>
            {type === 'section' ? (
              <Typography variant="body1">
                {name ||
                  t(`TrainingPlans.manageTranslations.noExistingTranslation`)}
              </Typography>
            ) : (
              <>
                <Typography variant="overline3">
                  {type === 'quiz' || type === 'quizStep'
                    ? t(`Generic.quizName`)
                    : type === 'question' && id
                      ? `${t('Generic.question')} ${id + 1}`
                      : t(
                          `Generic.${type as 'plan' | 'quiz' | 'section' | 'task' | 'question'}`,
                        )}
                </Typography>
                <Typography variant="body1">
                  {name ||
                    t(`TrainingPlans.manageTranslations.noExistingTranslation`)}
                </Typography>
              </>
            )}
          </StyledLeftSideSectionContainer>
          {type !== 'resource' && type !== 'quizStep' && (
            <StyledRightSideSectionContainer>
              <SquareIconButton
                data-testid="TranslationItemButton"
                disabled={!name}
                onClick={onTranslateName}
              >
                <IconWorld className="size-4!" />
              </SquareIconButton>
            </StyledRightSideSectionContainer>
          )}
        </StyledRowContainer>
        <StyledRowContainer>
          <IconCornerDownRight className="-mt-1! mr-2! size-8!" />
          {type === 'plan' && (
            <TextField
              fullWidth
              label={t(
                'TrainingPlans.manageTranslations.currentLanguageTranslation',
              )}
              name="plan"
              onChange={onChangeName}
              placeholder={t(
                'TrainingPlans.manageTranslations.nameTranslationPlaceholder',
              )}
              value={translation}
            />
          )}
          {type === 'quiz' && (
            <TextField
              fullWidth
              label={t(
                'TrainingPlans.manageTranslations.currentLanguageTranslation',
              )}
              name="quiz"
              onChange={onChangeName}
              placeholder={t(
                'TrainingPlans.manageTranslations.nameTranslationPlaceholder',
              )}
              value={translation}
            />
          )}
          {type === 'section' && (
            <TextField
              fullWidth
              label={t(
                'TrainingPlans.manageTranslations.currentLanguageTranslation',
              )}
              name="section"
              onChange={onChangeName}
              placeholder={t(
                'TrainingPlans.manageTranslations.sectionTranslationPlaceholder',
              )}
              value={translation}
            />
          )}
          {type === 'task' && (
            <TextField
              fullWidth
              label={t(
                'TrainingPlans.manageTranslations.currentLanguageTranslation',
              )}
              name="task"
              onChange={onChangeName}
              placeholder={t(
                'TrainingPlans.manageTranslations.taskTranslationPlaceholder',
              )}
              value={translation}
            />
          )}
          {type === 'question' && (
            <TextField
              fullWidth
              label={t(
                'TrainingPlans.manageTranslations.currentLanguageTranslation',
              )}
              name="question"
              onChange={onChangeName}
              placeholder={t(
                'TrainingPlans.manageTranslations.questionTranslationPlaceholder',
              )}
              value={translation}
            />
          )}
          {(type === 'resource' || type === 'quizStep') && (
            <Typography variant="body1" className={cn('self-center!')}>
              {translation || name}
            </Typography>
          )}
        </StyledRowContainer>
        <StyledChildrenContainer>{children}</StyledChildrenContainer>
      </StyledColumnContainer>
      {notes && (
        <StyledColumnContainer indented>
          <StyledRowContainer>
            <StyledLeftSideSectionContainer>
              <Typography variant="overline3">{t('Generic.notes')}</Typography>
              <Typography variant="body1">
                {notes ||
                  t(`TrainingPlans.manageTranslations.noExistingTranslation`)}
              </Typography>
            </StyledLeftSideSectionContainer>
            <StyledRightSideSectionContainer>
              <SquareIconButton onClick={onTranslateNotes}>
                <IconWorld className="size-4!" />
              </SquareIconButton>
            </StyledRightSideSectionContainer>
          </StyledRowContainer>
          <StyledRowContainer>
            <IconCornerDownRight className="-mt-1! mr-2! size-8!" />
            <TextField
              fullWidth
              label={t(
                'TrainingPlans.manageTranslations.currentLanguageTranslation',
              )}
              name="notes"
              onChange={onChangeNotes}
              placeholder={t(
                'TrainingPlans.manageTranslations.notesTranslationPlaceholder',
              )}
              value={notesTranslation ?? ''}
            />
          </StyledRowContainer>
        </StyledColumnContainer>
      )}
      {urls?.map((url, index) => {
        const link = url.urlSet?.[userLanguage];
        const linkTranslation = url.urlSet?.[targetLanguage];
        return (
          link && (
            <StyledColumnContainer
              indented
              key={`${url.urlSet?.[userLanguage]}${id}`}
            >
              <StyledRowContainer>
                <StyledLeftSideSectionContainer>
                  <Typography variant="overline3">
                    {t('Generic.link')}
                  </Typography>
                  <Typography variant="body1">
                    {link ||
                      t(
                        `TrainingPlans.manageTranslations.noExistingTranslation`,
                      )}
                  </Typography>
                </StyledLeftSideSectionContainer>
              </StyledRowContainer>
              <StyledRowContainer>
                <IconCornerDownRight className="-mt-1! mr-2! size-8!" />
                <TextField
                  fullWidth
                  name="link"
                  onChange={e => onChangeUrl?.(e, index)}
                  placeholder={t(
                    'TrainingPlans.manageTranslations.urlTranslationPlaceholder',
                  )}
                  value={linkTranslation ?? ''}
                />
              </StyledRowContainer>
            </StyledColumnContainer>
          )
        );
      })}
      {answers?.map((answerData, index) => (
        <StyledColumnContainer
          key={`${answerData.answer[userLanguage]}-${index}`}
        >
          <StyledRowContainer>
            <StyledLeftSideSectionContainer>
              <Typography variant="overline3">{`${t('Generic.answer')} ${
                index + 1
              }`}</Typography>
              <Typography variant="body1">
                {answerData.answer[userLanguage]}
              </Typography>
            </StyledLeftSideSectionContainer>
            <StyledRightSideSectionContainer>
              <SquareIconButton
                color="secondary"
                onClick={() => onTranslateAnswer?.(index)}
              >
                <IconWorld className="size-4!" />
              </SquareIconButton>
            </StyledRightSideSectionContainer>
          </StyledRowContainer>
          <StyledRowContainer>
            <IconCornerDownRight className="-mt-1! mr-2! size-8!" />
            <TextField
              fullWidth
              label={t(
                'TrainingPlans.manageTranslations.currentLanguageTranslation',
              )}
              name="answer"
              onChange={e => onChangeAnswer?.(e, index)}
              placeholder={t(
                'TrainingPlans.manageTranslations.answerTranslationPlaceholder',
              )}
              value={answerData.answer[targetLanguage] ?? ''}
            />
          </StyledRowContainer>
        </StyledColumnContainer>
      ))}
    </div>
  );
};

export default TranslationsItem;

// Tailwind functional replacements for previously styled components.
export const StyledColumnContainer: React.FC<
  ColumnContainerStyleProps & React.HTMLAttributes<HTMLDivElement>
> = ({ indented, className = '', children, ...rest }) => (
  <div
    className={cn(
      'my-6 flex w-full flex-col items-center first:mt-0 last:mb-0',
      indented && 'pl-10',
      className,
    )}
    {...rest}
  >
    {children}
  </div>
);

export const StyledRowContainer: React.FC<
  React.HTMLAttributes<HTMLDivElement>
> = ({ className = '', children, ...rest }) => (
  <div className={cn('flex w-full flex-row items-start', className)} {...rest}>
    {children}
  </div>
);

export const StyledLeftSideSectionContainer: React.FC<
  React.HTMLAttributes<HTMLDivElement>
> = ({ className = '', children, ...rest }) => (
  <div
    className={cn('flex flex-1 flex-col justify-start', className)}
    {...rest}
  >
    {children}
  </div>
);

export const StyledRightSideSectionContainer: React.FC<
  React.HTMLAttributes<HTMLDivElement>
> = ({ className = '', children, ...rest }) => (
  <div className={cn('ml-1 flex flex-col justify-start', className)} {...rest}>
    {children}
  </div>
);

export const StyledChildrenContainer: React.FC<
  React.HTMLAttributes<HTMLDivElement>
> = ({ className = '', children, ...rest }) => (
  <div className={cn('w-full', className)} {...rest}>
    {children}
  </div>
);
